import { Badge, Modal } from '@/components/ui';
import type { AsociacionNtcInfo, RequisitoAsociacion, TernaDTO } from '@/services/sysadmin.service';
import { renderWithBold } from '@/utils/renderWithBold';
import {
  Check,
  ChevronsDownUp,
  ChevronsUpDown,
  GripVertical,
  Info,
  Link,
  Link2,
  Pencil,
  Plus,
  Search,
  Trash2,
  Unlink,
  X,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';

// ========================================
// Tipos
// ========================================

/** Grupo visual de ternas (agrupadas por campo `grupo` o individuales) */
export interface GrupoTernas {
  id: string;
  grupo: string | null;
  filas: TernaDTO[];
}

// ========================================
// Helpers
// ========================================

/**
 * Agrupa ternas por el campo `grupo`. Ternas con grupo=null son independientes.
 */
export function buildGruposDesdeTernas(
  ternas: TernaDTO[],
  _requisitosSinTerna: string[],
  _todosRequisitos: RequisitoAsociacion[],
  _ntcs: AsociacionNtcInfo[]
): GrupoTernas[] {
  const grupoMap = new Map<string, TernaDTO[]>();
  const sinGrupo: TernaDTO[] = [];

  for (const terna of ternas) {
    if (terna.grupo) {
      if (!grupoMap.has(terna.grupo)) grupoMap.set(terna.grupo, []);
      grupoMap.get(terna.grupo)!.push(terna);
    } else {
      sinGrupo.push(terna);
    }
  }

  const grupos: GrupoTernas[] = [];

  // Grupos reales (2+ ternas con el mismo grupo)
  for (const [grupoId, ternasGrupo] of grupoMap) {
    grupos.push({
      id: grupoId,
      grupo: grupoId,
      filas: ternasGrupo.sort((a, b) => a.orden - b.orden || a.id - b.id),
    });
  }

  // Ternas sin grupo: cada una es su propio "grupo" de 1
  for (const terna of sinGrupo) {
    grupos.push({
      id: `solo-${terna.id}`,
      grupo: null,
      filas: [terna],
    });
  }

  // Ordenar grupos por el menor orden de sus filas
  grupos.sort((a, b) => a.filas[0].orden - b.filas[0].orden);

  return grupos;
}

// ========================================
// Helper: calcular spans (rowspan) para un grupo de ternas
// ========================================

interface SpanItem {
  startRow: number;
  span: number;
  req: RequisitoAsociacion | null;
  ternas: TernaDTO[];
}

/**
 * Para cada columna (NTC), agrupa filas consecutivas que tengan el mismo requisitoId.
 * Esto permite renderizar una sola celda que ocupe múltiples filas (rowspan visual).
 */
function calcularSpansGrupo(
  filas: TernaDTO[],
  ntcsOrdenadas: AsociacionNtcInfo[],
  requisitosMap: Map<string, RequisitoAsociacion>
): Record<number, SpanItem[]> {
  const spans: Record<number, SpanItem[]> = {};

  for (const ntc of ntcsOrdenadas) {
    const slotKey = `requisitoC${ntc.orden}Id` as 'requisitoC1Id' | 'requisitoC2Id' | 'requisitoC3Id';
    spans[ntc.ntcId] = [];
    let i = 0;
    while (i < filas.length) {
      const reqId = filas[i][slotKey];
      const req = reqId ? (requisitosMap.get(reqId) ?? null) : null;
      let j = i + 1;
      // Merge only non-null requisitos with the same id
      if (reqId) {
        while (j < filas.length && filas[j][slotKey] === reqId) {
          j++;
        }
      }
      spans[ntc.ntcId].push({
        startRow: i,
        span: j - i,
        req,
        ternas: filas.slice(i, j),
      });
      i = j;
    }
  }

  return spans;
}

// ========================================
// Modal para seleccionar requisito de una NTC específica
// ========================================

interface ModalSeleccionarRequisitoProps {
  isOpen: boolean;
  onClose: () => void;
  ntcNombre: string;
  ntcId: number;
  requisitosDisponibles: RequisitoAsociacion[];
  onSeleccionar: (requisitoId: string) => void;
}

function ModalSeleccionarRequisito({
  isOpen,
  onClose,
  ntcNombre,
  ntcId,
  requisitosDisponibles,
  onSeleccionar,
}: ModalSeleccionarRequisitoProps) {
  const [seleccionado, setSeleccionado] = useState<string>('');
  const [busqueda, setBusqueda] = useState('');

  React.useEffect(() => {
    if (isOpen) {
      setSeleccionado('');
      setBusqueda('');
    }
  }, [isOpen]);

  const filtrados = useMemo(() => {
    return requisitosDisponibles
      .filter((r) => r.ntcId === ntcId)
      .filter((r) => {
        if (!busqueda) return true;
        const q = busqueda.toLowerCase();
        return r.numeral.toLowerCase().includes(q) || r.capitulo.toLowerCase().includes(q) || r.descripcion.toLowerCase().includes(q);
      });
  }, [requisitosDisponibles, ntcId, busqueda]);

  const handleConfirmar = () => {
    if (seleccionado) onSeleccionar(seleccionado);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Asignar requisito — ${ntcNombre}`} size="xl">
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            Selecciona un requisito de <span className="font-bold">{ntcNombre}</span> para asignar a esta celda.
          </p>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por numeral, capítulo o descripción..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
          {filtrados.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">
              {busqueda ? 'No se encontraron requisitos con los filtros' : 'No hay requisitos disponibles para esta NTC'}
            </div>
          ) : (
            filtrados.map((r) => {
              const isSelected = seleccionado === r.requisitoId;
              return (
                <label
                  key={r.requisitoId}
                  className={`flex items-start gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${
                    isSelected ? 'bg-blue-50' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="req-selection"
                    checked={isSelected}
                    onChange={() => setSeleccionado(r.requisitoId)}
                    className="h-4 w-4 mt-0.5 shrink-0 border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="min-w-0 flex-1">
                    <span className="font-semibold text-sm text-gray-900">
                      {r.capitulo} {r.numeral}
                    </span>
                    <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">{r.descripcion}</p>
                  </div>
                </label>
              );
            })
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirmar}
            disabled={!seleccionado}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <Check className="w-4 h-4" />
            Asignar
          </button>
        </div>
      </div>
    </Modal>
  );
}

const RequisitoCellReadonly = React.memo<{
  req: RequisitoAsociacion;
  expandAll?: boolean;
  onEditar?: (req: RequisitoAsociacion) => void;
  selectionMode?: boolean;
  onSelectTarget?: (reqId: string) => void;
  sourceNtcId?: number;
  onContextMenu?: (e: React.MouseEvent, req: RequisitoAsociacion) => void;
}>(({ req, expandAll, onEditar, selectionMode, onSelectTarget, sourceNtcId, onContextMenu }) => {
  const [descExpanded, setDescExpanded] = useState(false);
  const isExpanded = expandAll || descExpanded;

  return (
    <div
      className={`flex flex-col justify-start h-full group/cell relative p-1 rounded-md transition-all ${
        selectionMode && (sourceNtcId === undefined || req.ntcId === sourceNtcId)
          ? 'cursor-pointer hover:bg-blue-50 hover:ring-1 hover:ring-blue-300 shadow-xs'
          : ''
      }`}
      onClick={() =>
        selectionMode && onSelectTarget && (sourceNtcId === undefined || req.ntcId === sourceNtcId) && onSelectTarget(req.requisitoId)
      }
      onContextMenu={(e) => onContextMenu && onContextMenu(e, req)}
    >
      <div className="flex items-start justify-between gap-1">
        <div>
          <div className="font-bold text-sm text-gray-900">{req.capitulo}</div>
          <div className="font-bold text-sm text-gray-900" title={`ID: ${req.requisitoId}`}>
            {req.numeral}
          </div>
        </div>
        {!selectionMode && onEditar && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEditar(req);
            }}
            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors shrink-0"
            title="Editar requisito"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
        {selectionMode && onSelectTarget && (
          <div className="absolute inset-0 bg-blue-50/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10 rounded">
            {sourceNtcId !== undefined && req.ntcId !== sourceNtcId ? (
              <div className="bg-gray-400 text-white text-[9px] px-2 py-1 rounded shadow-sm cursor-not-allowed">NTC Distinta</div>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectTarget(req.requisitoId);
                }}
                className="bg-blue-600 text-white text-[9px] px-2 py-1 rounded shadow-sm hover:bg-blue-700 transition-colors"
              >
                Seleccionar
              </button>
            )}
          </div>
        )}
      </div>
      <div className={`text-sm text-gray-700 mt-1 whitespace-pre-wrap ${isExpanded ? '' : 'line-clamp-3'}`}>
        {renderWithBold(req.descripcion)}
      </div>
      {!expandAll && (
        <button
          type="button"
          onClick={() => setDescExpanded((v) => !v)}
          className="text-xs text-blue-600 hover:text-blue-800 font-medium mt-0.5 self-start"
        >
          {descExpanded ? 'Ver menos' : 'Ver más'}
        </button>
      )}
    </div>
  );
});
RequisitoCellReadonly.displayName = 'RequisitoCellReadonly';

// ========================================
// Celda editable con requisito asignado
// ========================================

interface CeldaConRequisitoProps {
  req: RequisitoAsociacion;
  ternas: TernaDTO[];
  columnaOrden: number;
  onDesvincular: (ternaId: number, columnaOrden: number) => void;
  onEditar?: (req: RequisitoAsociacion) => void;
  expandAll?: boolean;
  selectionMode?: boolean;
  onSelectTarget?: (reqId: string) => void;
  sourceNtcId?: number;
  onContextMenu?: (e: React.MouseEvent, req: RequisitoAsociacion) => void;
}

const CeldaConRequisito = React.memo<CeldaConRequisitoProps>(
  ({ req, ternas, columnaOrden, onDesvincular, onEditar, expandAll, selectionMode, onSelectTarget, sourceNtcId, onContextMenu }) => {
    const [descExpanded, setDescExpanded] = useState(false);
    const isExpanded = expandAll || descExpanded;

    return (
      <div
        className={`relative flex flex-col justify-start h-full group/cell p-1 rounded-md transition-all ${
          selectionMode && (sourceNtcId === undefined || req.ntcId === sourceNtcId)
            ? 'cursor-pointer hover:bg-blue-50 hover:ring-1 hover:ring-blue-300 shadow-xs'
            : ''
        }`}
        onClick={() =>
          selectionMode && onSelectTarget && (sourceNtcId === undefined || req.ntcId === sourceNtcId) && onSelectTarget(req.requisitoId)
        }
        onContextMenu={(e) => onContextMenu && onContextMenu(e, req)}
      >
        <div className="flex-1">
          <div className="flex items-start justify-between gap-1">
            <div>
              <div className="font-bold text-sm text-gray-900">{req.capitulo}</div>
              <div className="font-bold text-sm text-gray-900" title={`ID: ${req.requisitoId}`}>
                {req.numeral}
              </div>
            </div>
            {!selectionMode && onEditar && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditar(req);
                }}
                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors shrink-0"
                title="Editar requisito"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            {selectionMode && onSelectTarget && (
              <div className="absolute inset-0 bg-blue-50/10 flex items-center justify-center opacity-0 group-hover/cell:opacity-100 transition-opacity z-10 rounded">
                {sourceNtcId !== undefined && req.ntcId !== sourceNtcId ? (
                  <div className="bg-gray-400 text-white text-[9px] px-2 py-1 rounded shadow-sm cursor-not-allowed">NTC Distinta</div>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectTarget(req.requisitoId);
                    }}
                    className="bg-blue-600 text-white text-[9px] px-2 py-1 rounded shadow-sm hover:bg-blue-700 transition-colors"
                  >
                    Seleccionar
                  </button>
                )}
              </div>
            )}
          </div>
          <div className={`text-sm text-gray-700 mt-1 whitespace-pre-wrap ${isExpanded ? '' : 'line-clamp-3'}`}>
            {renderWithBold(req.descripcion)}
          </div>
          {!expandAll && (
            <button
              type="button"
              onClick={() => setDescExpanded((v) => !v)}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium mt-0.5"
            >
              {descExpanded ? 'Ver menos' : 'Ver más'}
            </button>
          )}
        </div>
        <div className="mt-2 flex flex-wrap justify-end gap-1">
          {ternas.map((terna) => (
            <button
              key={terna.id}
              type="button"
              onClick={() => onDesvincular(terna.id, columnaOrden)}
              className="flex items-center gap-1 p-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-300 rounded hover:bg-red-100 transition-colors"
              title={ternas.length > 1 ? `Quitar de terna #${terna.id}` : 'Quitar requisito de esta celda'}
            >
              <X className="w-3.5 h-3.5" />
              {ternas.length > 1 ? `T${terna.id}` : 'Quitar'}
            </button>
          ))}
        </div>
      </div>
    );
  }
);
CeldaConRequisito.displayName = 'CeldaConRequisito';

// ========================================
// Celda vacía editable (botón para asignar)
// ========================================

interface CeldaVaciaEditableProps {
  ternaId: number;
  columnaOrden: number;
  ntcNombre: string;
  ntcId: number;
  todosRequisitos: RequisitoAsociacion[];
  onAsignar: (ternaId: number, columnaOrden: number, requisitoId: string) => void;
}

const CeldaVaciaEditable = React.memo<CeldaVaciaEditableProps>(
  ({ ternaId, columnaOrden, ntcNombre, ntcId, todosRequisitos, onAsignar }) => {
    const [modalOpen, setModalOpen] = useState(false);

    return (
      <>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="w-full h-full min-h-15 flex flex-col items-center justify-center gap-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50/50 rounded transition-colors border-2 border-dashed border-gray-200 hover:border-blue-300"
          title={`Asignar requisito de ${ntcNombre}`}
        >
          <Plus className="w-5 h-5" />
          <span className="text-xs font-medium">Asignar</span>
        </button>

        {modalOpen && (
          <ModalSeleccionarRequisito
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            ntcNombre={ntcNombre}
            ntcId={ntcId}
            requisitosDisponibles={todosRequisitos}
            onSeleccionar={(requisitoId) => onAsignar(ternaId, columnaOrden, requisitoId)}
          />
        )}
      </>
    );
  }
);
CeldaVaciaEditable.displayName = 'CeldaVaciaEditable';

// ========================================
// Componente principal: MatrizAsociaciones
// ========================================

interface MatrizAsociacionesProps {
  grupos: GrupoTernas[];
  ntcs: AsociacionNtcInfo[];
  todosRequisitos?: RequisitoAsociacion[];
  editable?: boolean;
  onAsignarRequisito?: (ternaId: number, columnaOrden: number, requisitoId: string) => void;
  onDesvincularRequisito?: (ternaId: number, columnaOrden: number) => void;
  onReordenar?: (reorden: { id: number; orden: number }[]) => void;
  onEliminarTerna?: (ternaIds: number[]) => void;
  onAgruparTernas?: (ternaIds: number[]) => void;
  onDesagruparTerna?: (ternaId: number) => void;
  onCrearTerna?: (requisitoId: string, columnaOrden: number) => void;
  onInsertarTerna?: (requisitoId: string, columnaOrden: number, insertAtOrden: number) => void;
  onEditarRequisito?: (requisito: RequisitoAsociacion) => void;
  selectionMode?: boolean;
  onSelectTarget?: (requisitoId: string) => void;
  sourceNtcId?: number;
  onShowAnalysis?: (requisitoId: string) => void;
  onIniciarMigracion?: (requisitoId: string) => void;
}

export function MatrizAsociaciones({
  grupos,
  ntcs,
  todosRequisitos,
  editable,
  onAsignarRequisito,
  onDesvincularRequisito,
  onReordenar,
  onEliminarTerna,
  onAgruparTernas,
  onDesagruparTerna,
  onCrearTerna,
  onInsertarTerna,
  onEditarRequisito,
  selectionMode,
  onSelectTarget,
  sourceNtcId,
  onShowAnalysis,
  onIniciarMigracion,
}: MatrizAsociacionesProps) {
  const ntcsOrdenadas = useMemo(() => [...ntcs].sort((a, b) => a.orden - b.orden), [ntcs]);
  const [expandAll, setExpandAll] = useState(false);
  const [seleccionParaAgrupar, setSeleccionParaAgrupar] = useState<Set<number>>(new Set());
  const [draggedGroupIdx, setDraggedGroupIdx] = useState<number | null>(null);
  const [dragOverGroupIdx, setDragOverGroupIdx] = useState<number | null>(null);
  const [modalCrearOpen, setModalCrearOpen] = useState(false);
  const [creatingColOrden, setCreatingColOrden] = useState<number | null>(null);
  const [insertingAtOrden, setInsertingAtOrden] = useState<number | null>(null);

  // Estado para Menú Contextual
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    requisito: RequisitoAsociacion;
  } | null>(null);

  const containerRef = React.useRef<HTMLDivElement>(null);

  const colTemplate = 'minmax(250px, 1fr)';
  const gridCols = ntcsOrdenadas.map(() => colTemplate).join(' ') + (editable ? ' 100px' : '');

  // Flatten all ternas across all groups into a single list with metadata
  const flatRows = useMemo(() => {
    const rows: {
      terna: TernaDTO;
      grupoIdx: number;
      grupo: GrupoTernas;
      filaIdxInGrupo: number;
      isFirstInGrupo: boolean;
      isLastInGrupo: boolean;
      isGrupoReal: boolean;
    }[] = [];
    for (let gi = 0; gi < grupos.length; gi++) {
      const grupo = grupos[gi];
      const isGrupoReal = grupo.grupo !== null && grupo.filas.length > 1;
      for (let fi = 0; fi < grupo.filas.length; fi++) {
        rows.push({
          terna: grupo.filas[fi],
          grupoIdx: gi,
          grupo,
          filaIdxInGrupo: fi,
          isFirstInGrupo: fi === 0,
          isLastInGrupo: fi === grupo.filas.length - 1,
          isGrupoReal,
        });
      }
    }
    return rows;
  }, [grupos]);

  // Build a map for quick requisito lookups by ID
  const requisitosMap = useMemo(() => new Map((todosRequisitos ?? []).map((r) => [r.requisitoId, r])), [todosRequisitos]);

  // Compute spans globally across all flat rows
  const globalSpans = useMemo(() => {
    return calcularSpansGrupo(
      flatRows.map((r) => r.terna),
      ntcsOrdenadas,
      requisitosMap
    );
  }, [flatRows, ntcsOrdenadas, requisitosMap]);

  const handleContextMenu = useCallback((e: React.MouseEvent, req: RequisitoAsociacion) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      requisito: req,
    });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleDesvincular = useCallback(
    (ternaId: number, columnaOrden: number) => {
      if (!onDesvincularRequisito) return;
      onDesvincularRequisito(ternaId, columnaOrden);
    },
    [onDesvincularRequisito]
  );

  const handleAsignar = useCallback(
    (ternaId: number, columnaOrden: number, requisitoId: string) => {
      if (!onAsignarRequisito) return;
      onAsignarRequisito(ternaId, columnaOrden, requisitoId);
    },
    [onAsignarRequisito]
  );

  const escListenerRef = React.useRef<(ke: KeyboardEvent) => void | null>(null);

  const handleDragStart = (e: React.DragEvent, idx: number) => {
    setDraggedGroupIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox requires some data to be set
    e.dataTransfer.setData('text/plain', idx.toString());

    // attach escape key listener once
    const onKey = (ke: KeyboardEvent) => {
      if (ke.key === 'Escape') {
        setDraggedGroupIdx(null);
        setDragOverGroupIdx(null);
        if (escListenerRef.current) {
          window.removeEventListener('keydown', escListenerRef.current);
          escListenerRef.current = null;
        }
      }
    };
    escListenerRef.current = onKey;
    window.addEventListener('keydown', onKey);

    // Optional: make the drag image look a bit translucent
    setTimeout(() => {
      if (e.target instanceof HTMLElement) {
        e.target.style.opacity = '0.5';
      }
    }, 0);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setDraggedGroupIdx(null);
    setDragOverGroupIdx(null);
    if (e.target instanceof HTMLElement) {
      e.target.style.opacity = '1';
    }
    // remove escape listener if still attached
    if (escListenerRef.current) {
      window.removeEventListener('keydown', escListenerRef.current);
      escListenerRef.current = null;
    }
  };

  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedGroupIdx !== null && draggedGroupIdx !== idx) {
      setDragOverGroupIdx(idx);
    }

    // auto-scroll relative to the component container if possible
    // if dragging over the small grab handle, use tighter threshold
    const isOverHandle = (e.target as HTMLElement).closest('.drag-handle') !== null;
    const THRESHOLD = isOverHandle ? 10 : 100; // pixels from edge
    const { clientY } = e;
    const container = containerRef.current;

    // DEBUG logging
    console.log('dragOver', { idx, clientY, isOverHandle, draggedGroupIdx });

    if (container) {
      const rect = container.getBoundingClientRect();
      const offsetY = clientY - rect.top;
      console.log('offsetY', offsetY, 'rect.height', rect.height);
      if (offsetY < THRESHOLD) {
        const delta = THRESHOLD - offsetY;
        console.log('scroll up delta', delta);
        window.scrollBy({ top: -Math.ceil(delta * 0.5), behavior: 'smooth' });
      } else if (offsetY > rect.height - THRESHOLD) {
        const delta = offsetY - (rect.height - THRESHOLD);
        console.log('scroll down delta', delta);
        window.scrollBy({ top: Math.ceil(delta * 0.5), behavior: 'smooth' });
      }
    } else {
      // fallback to window
      const { innerHeight } = window;
      if (clientY < THRESHOLD) {
        const delta = THRESHOLD - clientY;
        window.scrollBy({ top: -Math.ceil(delta * 0.5), behavior: 'smooth' });
      } else if (clientY > innerHeight - THRESHOLD) {
        const delta = clientY - (innerHeight - THRESHOLD);
        window.scrollBy({ top: Math.ceil(delta * 0.5), behavior: 'smooth' });
      }
    }
  };

  const handleDrop = (e: React.DragEvent, targetIdx: number) => {
    e.preventDefault();

    if (draggedGroupIdx === null || draggedGroupIdx === targetIdx || !onReordenar) {
      setDraggedGroupIdx(null);
      setDragOverGroupIdx(null);
      return;
    }

    const newGrupos = [...grupos];
    const [moved] = newGrupos.splice(draggedGroupIdx, 1);
    newGrupos.splice(targetIdx, 0, moved);

    // Flatten all ternas para asegurar que cada una reciba un orden estrictamente único
    const allFilas = grupos.flatMap((g) => g.filas);
    const existingOrders = allFilas.map((f) => f.orden).sort((a, b) => a - b);

    // Ahora hacemos el flatten visual del nuevo ordenamiento
    const newFilas = newGrupos.flatMap((g) => g.filas);
    const reorden: { id: number; orden: number }[] = [];

    newFilas.forEach((f, idx) => {
      const assignedOrder = existingOrders[idx];
      if (f.orden !== assignedOrder) {
        reorden.push({ id: f.id, orden: assignedOrder });
      }
    });

    if (reorden.length > 0) {
      onReordenar(reorden);
    }

    setDraggedGroupIdx(null);
    setDragOverGroupIdx(null);
  };

  const toggleSeleccionAgrupar = (ternaId: number) => {
    setSeleccionParaAgrupar((prev) => {
      const next = new Set(prev);
      if (next.has(ternaId)) next.delete(ternaId);
      else next.add(ternaId);
      return next;
    });
  };

  const handleAgruparSeleccionados = () => {
    if (!onAgruparTernas || seleccionParaAgrupar.size < 2) return;
    onAgruparTernas(Array.from(seleccionParaAgrupar));
    setSeleccionParaAgrupar(new Set());
  };

  return (
    <div className="space-y-2">
      {/* Toolbar de agrupación */}
      {editable && onAgruparTernas && (
        <div className="flex items-center gap-3 px-2">
          {seleccionParaAgrupar.size >= 2 ? (
            <button
              type="button"
              onClick={handleAgruparSeleccionados}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-300 rounded-lg hover:bg-green-100 transition-colors"
            >
              <Link className="w-4 h-4" />
              Juntar {seleccionParaAgrupar.size} ternas seleccionadas
            </button>
          ) : (
            <p className="text-xs text-gray-400">
              Marca las ternas que quieras juntar con el checkbox de la columna &quot;Acciones&quot; y presiona &quot;Juntar&quot;.
            </p>
          )}
          {seleccionParaAgrupar.size > 0 && (
            <button
              type="button"
              onClick={() => setSeleccionParaAgrupar(new Set())}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Limpiar selección
            </button>
          )}
        </div>
      )}

      <div ref={containerRef} className="overflow-x-auto overflow-y-hidden w-full border border-gray-300 rounded-lg">
        <div style={{ display: 'table', minWidth: '100%' }}>
          {/* Header */}
          <div className="grid z-20 bg-gray-100 border-b-2 border-gray-300" style={{ gridTemplateColumns: gridCols }}>
            {ntcsOrdenadas.map((ntc, idx) => (
              <div key={ntc.ntcId} className="p-4 font-semibold text-gray-800 flex items-center justify-between">
                <span>{ntc.nombre}</span>

                <div className="flex items-center gap-1">
                  {editable && onCrearTerna && (
                    <button
                      type="button"
                      onClick={() => {
                        setCreatingColOrden(ntc.orden);
                        setModalCrearOpen(true);
                      }}
                      className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
                      title="Agregar terna en esta columna"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  )}
                  {idx === 0 && (
                    <button
                      type="button"
                      onClick={() => setExpandAll((v) => !v)}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                      title={expandAll ? 'Colapsar descripciones' : 'Expandir descripciones'}
                    >
                      {expandAll ? <ChevronsDownUp className="w-3.5 h-3.5" /> : <ChevronsUpDown className="w-3.5 h-3.5" />}
                      {expandAll ? 'Colapsar' : 'Expandir'}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {editable && <div className="p-4 font-semibold text-gray-800 text-center">Acciones</div>}
          </div>

          {/* Single flat grid for all rows (allows cross-grupo rowspan) */}
          {flatRows.length > 0 ? (
            <div
              className="grid"
              style={{
                gridTemplateColumns: gridCols,
                gridTemplateRows: `repeat(${flatRows.length}, auto)`,
              }}
            >
              {/* Celdas de requisitos (con rowspan global) */}
              {ntcsOrdenadas.map((ntc, colIdx) =>
                globalSpans[ntc.ntcId].map((item, spanIdx) => {
                  // Background: green if the first terna in the span has requisitos in >1 NTC
                  const sampleTerna = item.ternas[0];
                  const ntcsConReq = ntcsOrdenadas.filter((n) => {
                    const key = `requisitoC${n.orden}Id` as 'requisitoC1Id' | 'requisitoC2Id' | 'requisitoC3Id';
                    return sampleTerna[key] !== null;
                  }).length;
                  const cellBg = ntcsConReq > 1 ? 'bg-green-50/50' : 'bg-white';

                  // Bottom border: grupo separator takes priority, otherwise normal row border
                  const spanEnd = item.startRow + item.span;
                  const lastRowInSpan = flatRows[spanEnd - 1];
                  const isAtGrupoBoundary = lastRowInSpan?.isLastInGrupo && spanEnd < flatRows.length;
                  const borderBottom =
                    spanEnd < flatRows.length
                      ? isAtGrupoBoundary
                        ? lastRowInSpan.isGrupoReal || flatRows[spanEnd]?.isGrupoReal
                          ? '3px solid #60a5fa'
                          : '3px solid #9ca3af'
                        : '3px solid #e5e7eb'
                      : undefined;

                  return (
                    <div
                      key={`${ntc.ntcId}-${spanIdx}`}
                      className={`p-4 ${cellBg} ${colIdx < ntcsOrdenadas.length - 1 ? 'border-r border-gray-200' : ''}`}
                      style={{
                        gridColumn: colIdx + 1,
                        gridRow: `${item.startRow + 1} / span ${item.span}`,
                        borderBottom,
                      }}
                    >
                      {item.req ? (
                        editable && onDesvincularRequisito ? (
                          <CeldaConRequisito
                            req={item.req}
                            ternas={item.ternas}
                            columnaOrden={ntc.orden}
                            onDesvincular={handleDesvincular}
                            onEditar={onEditarRequisito}
                            expandAll={expandAll}
                            selectionMode={selectionMode}
                            onSelectTarget={onSelectTarget}
                            sourceNtcId={sourceNtcId}
                            onContextMenu={handleContextMenu}
                          />
                        ) : (
                          <RequisitoCellReadonly
                            req={item.req}
                            onEditar={onEditarRequisito}
                            expandAll={expandAll}
                            selectionMode={selectionMode}
                            onSelectTarget={onSelectTarget}
                            sourceNtcId={sourceNtcId}
                            onContextMenu={handleContextMenu}
                          />
                        )
                      ) : editable && onAsignarRequisito && todosRequisitos ? (
                        <CeldaVaciaEditable
                          ternaId={item.ternas[0].id}
                          columnaOrden={ntc.orden}
                          ntcNombre={ntc.nombre}
                          ntcId={ntc.ntcId}
                          todosRequisitos={todosRequisitos}
                          onAsignar={handleAsignar}
                        />
                      ) : (
                        <div className="text-gray-400 text-sm italic">—</div>
                      )}
                    </div>
                  );
                })
              )}

              {/* Columna de acciones (una por fila) */}
              {editable &&
                flatRows.map((row, flatIdx) => {
                  const { terna, grupoIdx, grupo, filaIdxInGrupo, isLastInGrupo, isGrupoReal } = row;
                  const nextRow = flatRows[flatIdx + 1];
                  const isAtGrupoBoundary = isLastInGrupo && flatIdx < flatRows.length - 1;
                  const borderBottom = isAtGrupoBoundary
                    ? isGrupoReal || nextRow?.isGrupoReal
                      ? '3px solid #60a5fa'
                      : '3px solid #9ca3af'
                    : flatIdx < flatRows.length - 1
                      ? '1px solid #e5e7eb'
                      : undefined;

                  return (
                    <React.Fragment key={`actions-fragment-${terna.id}`}>
                      <div
                        className={`bg-gray-50 flex flex-col items-center justify-center gap-1.5 p-2 transition-all relative group/row-actions ${
                          dragOverGroupIdx === grupoIdx
                            ? draggedGroupIdx !== null && draggedGroupIdx < grupoIdx
                              ? 'border-b-4 border-b-blue-500' // dragging downwards, highlight bottom
                              : 'border-t-4 border-t-blue-500' // dragging upwards, highlight top
                            : ''
                        }`}
                        style={{
                          gridColumn: ntcsOrdenadas.length + 1,
                          gridRow: flatIdx + 1,
                          borderBottom: borderBottom && dragOverGroupIdx !== grupoIdx ? borderBottom : undefined,
                        }}
                        onDragOver={filaIdxInGrupo === 0 ? (e) => handleDragOver(e, grupoIdx) : undefined}
                        onDrop={filaIdxInGrupo === 0 ? (e) => handleDrop(e, grupoIdx) : undefined}
                      >
                        {onAgruparTernas && (
                          <label className="flex items-center gap-1 cursor-pointer" title="Seleccionar para juntar">
                            <input
                              type="checkbox"
                              checked={seleccionParaAgrupar.has(terna.id)}
                              onChange={() => toggleSeleccionAgrupar(terna.id)}
                              className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                            />
                            <span className="text-[10px] text-gray-500">Sel.</span>
                          </label>
                        )}

                        {onReordenar && filaIdxInGrupo === 0 && (
                          <div
                            draggable
                            onDragStart={(e) => handleDragStart(e, grupoIdx)}
                            onDragEnd={handleDragEnd}
                            className="drag-handle flex items-center justify-center p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded cursor-grab active:cursor-grabbing"
                            title="Arrastrar para reordenar"
                          >
                            <GripVertical className="w-5 h-5" />
                          </div>
                        )}

                        {onDesagruparTerna && terna.grupo && grupo.filas.length > 1 && (
                          <button
                            type="button"
                            onClick={() => onDesagruparTerna(terna.id)}
                            className="p-1 text-orange-500 hover:text-orange-700 hover:bg-orange-50 rounded"
                            title="Separar esta terna del grupo"
                          >
                            <Unlink className="w-4 h-4" />
                          </button>
                        )}

                        {onEliminarTerna && filaIdxInGrupo === 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              const ids = grupo.filas.map((f) => f.id);
                              if (ids.length > 0) onEliminarTerna(ids);
                            }}
                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                            title="Eliminar terna(s)"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}

                        {/* Grupo label indicator */}
                        {isGrupoReal && filaIdxInGrupo === 0 && (
                          <div className="flex items-center gap-0.5 mt-1" title="Grupo">
                            <Link className="w-3 h-3 text-blue-500" />
                            <span className="text-[9px] text-blue-500 font-medium">{grupo.filas.length}T</span>
                          </div>
                        )}

                        {/* Hover + button for insertion (ONLY if onInsertarTerna provided) */}
                        {onInsertarTerna && isLastInGrupo && flatIdx < flatRows.length - 1 && (
                          <div
                            className="absolute -bottom-3 left-1/2 -translate-x-1/2 z-30 opacity-0 group-hover/row-actions:opacity-100 transition-opacity"
                            title="Insertar terna aquí"
                          >
                            <button
                              type="button"
                              onClick={() => {
                                // Insert BEFORE the next row, so use nextRow terna's orden
                                setCreatingColOrden(1); // Default to first col
                                setInsertingAtOrden(nextRow.terna.orden);
                                setModalCrearOpen(true);
                              }}
                              className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-lg hover:bg-blue-700 transition-colors transform hover:scale-110"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-400">No hay ternas para esta combinación de NTCs</div>
          )}
        </div>
      </div>

      {/* Botón crear terna */}
      {/* no global create button; each header column has its own + */}

      {modalCrearOpen && creatingColOrden !== null && (
        <ModalSeleccionarRequisito
          isOpen={modalCrearOpen}
          onClose={() => {
            setModalCrearOpen(false);
            setCreatingColOrden(null);
            setInsertingAtOrden(null);
          }}
          ntcNombre={ntcsOrdenadas.find((n) => n.orden === (creatingColOrden || 1))?.nombre || ''}
          ntcId={ntcsOrdenadas.find((n) => n.orden === (creatingColOrden || 1))?.ntcId || 0}
          requisitosDisponibles={todosRequisitos || []}
          onSeleccionar={(requisitoId) => {
            if (creatingColOrden !== null) {
              if (insertingAtOrden !== null && onInsertarTerna) {
                onInsertarTerna(requisitoId, creatingColOrden, insertingAtOrden);
              } else if (onCrearTerna) {
                onCrearTerna(requisitoId, creatingColOrden);
              }
            }
            setModalCrearOpen(false);
            setCreatingColOrden(null);
            setInsertingAtOrden(null);
          }}
        />
      )}

      {/* Menú Contextual */}
      {contextMenu && (
        <>
          <div
            className="fixed inset-0 z-100"
            onClick={closeContextMenu}
            onContextMenu={(e) => {
              e.preventDefault();
              closeContextMenu();
            }}
          />
          <div
            className="fixed bg-white border border-gray-200 rounded-lg shadow-2xl z-110 overflow-hidden min-w-48 animate-in fade-in zoom-in duration-100"
            style={{
              top: contextMenu.y,
              left: contextMenu.x,
              transform: contextMenu.x + 200 > window.innerWidth ? 'translateX(-100%)' : '',
            }}
          >
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{contextMenu.requisito.numeral}</span>
              <Badge variant="blue" className="text-[8px] py-0 px-1">
                {ntcsOrdenadas.find((n) => n.ntcId === contextMenu.requisito.ntcId)?.nombre}
              </Badge>
            </div>
            <button
              onClick={() => {
                onShowAnalysis && onShowAnalysis(contextMenu.requisito.requisitoId);
                closeContextMenu();
              }}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-left text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
            >
              <Info className="w-3.5 h-3.5" />
              Análisis detallado
            </button>
            <button
              onClick={() => {
                onIniciarMigracion && onIniciarMigracion(contextMenu.requisito.requisitoId);
                closeContextMenu();
              }}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-left text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
            >
              <Link2 className="w-3.5 h-3.5" />
              Fusionar / Migrar
            </button>
            {onEditarRequisito && (
              <button
                onClick={() => {
                  onEditarRequisito(contextMenu.requisito);
                  closeContextMenu();
                }}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-left text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors border-t border-gray-50"
              >
                <Pencil className="w-3.5 h-3.5" />
                Editar requisito
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
